<!--
* Lays out baggage item form
*
* @param {BaggageItem} currentItem - Current item object if editing
* @param {number} currentItemIndex - Current items index in array
* @param {boolean} addToClaim - Whether to add item to claim
*
* @fires MsBaggageItemForm#imageValidityCheck
* @fires MsBaggageItemForm#purchaseDateChange
-->
<!--
* Emits image validity check
*
* @event MsBaggageItemForm#imageValidityCheck
-->

<!--
* Emits purchase date change
*
* @event MsBaggageItemForm#purchaseDateChange
-->

<template>
  <div>
    <!-- Baggage type -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.type`) }}</label>
      <p class="control is-expanded has-icons-right"></p>
      <div class="select is-fullwidth">
        <AppSelect
          v-model="type"
          v-validate:type="'required'"
          :data-vv-as="$t(`${pagei18nKey}.type`)"
          displayKey="name"
          :placeholder="$t('message.selectPlaceholderText')"
          class="is-fullwidth"
          :options="types"
          :class="{'is-danger': errors.has('type')}"
          name="type"/>
      </div>
      <p
        v-show="errors.has('type')"
        class="help is-danger">
        {{ errors.first('type') }}
      </p>
    </div>

    <!-- Model - shown for buggy/mobility types only -->
    <div v-show="isBuggyType === true" class="field">
      <label class="label">{{ $t(`${pagei18nKey}.model`) }}</label>
      <div class="control has-icons-right">
        <input
          v-model="baggageModel"
          v-validate="{required: isBuggyType}"
          class="input"
          :class="{'is-danger': errors.has('baggageModel') }"
          :data-vv-as="$t(`${pagei18nKey}.model`)"
          name="baggageModel"
          type="text"
          :placeholder="$t(`${pagei18nKey}.model`)"
          autocomplete="off"
          autocapitalize="none">
        <span
          v-show="fields.baggageModel && fields.baggageModel.valid"
          class="icon is-small is-right">
          <i class="fas fa-check"></i>
        </span>
      </div>
      <p
        v-show="errors.has('baggageModel')"
        class="help is-danger">
        {{ errors.first('baggageModel') }}
      </p>
    </div>

    <!-- Serial number - shown for buggy/mobility types only -->
    <div v-show="isBuggyType === true" class="field">
      <label class="label">{{ $t(`${pagei18nKey}.serialNumber`) }}</label>
      <div class="control has-icons-right">
        <input
          v-model="serialNumber"
          class="input"
          :class="{'is-danger': errors.has('serialNumber') }"
          :data-vv-as="$t(`${pagei18nKey}.serialNumber`)"
          name="serialNumber"
          type="text"
          :placeholder="$t(`${pagei18nKey}.serialNumber`)"
          autocomplete="off"
          autocapitalize="none">
        <span
          v-show="fields.serialNumber && fields.serialNumber.valid"
          class="icon is-small is-right">
          <i class="fas fa-check"></i>
        </span>
      </div>
      <p
        v-show="errors.has('serialNumber')"
        class="help is-danger">
        {{ errors.first('serialNumber') }}
      </p>
    </div>

    <!-- Is expandable buttons -->
    <div v-show="isBuggyType === false" class="field">
      <label class="label">{{ $t(`${pagei18nKey}.isExpandable`) }}</label>
      <ms-navigation-bar
        :buttons="[ expandableButton, notExpandableButton ]"
        @navigationButtonClick="expandableButtonClick"
      />
    </div>

    <!-- Tag number -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.tagNumber`) }}</label>
      <div class="control has-icons-right">
        <input
          v-model="tagNumber"
          v-validate="{ required: tagNumberRequired }"
          class="input"
          :class="{'is-danger': errors.has('tagNumber') }"
          :data-vv-as="$t(`${pagei18nKey}.tagNumber`)"
          name="tagNumber"
          type="text"
          :placeholder="$t(`${pagei18nKey}.tagNumber`)"
          autocomplete="off"
          autocapitalize="none">
        <span
          v-show="fields.tagNumber && fields.tagNumber.valid"
          class="icon is-small is-right">
          <i class="fas fa-check"></i>
        </span>
      </div>
      <p
        v-show="errors.has('tagNumber')"
        class="help is-danger">
        {{ errors.first('tagNumber') }}
      </p>
    </div>

    <!-- Brand -->
    <!-- todo add other option that shows text field -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.brand`) }}</label>
      <p class="control is-expanded has-icons-right"></p>
      <div class="is-fullwidth">
        <AppSelect
          v-model="brand"
          v-on:change="onBrandChanged"
          v-validate:brand="'required'"
          :data-vv-as="$t(`${pagei18nKey}.brand`)"
          displayKey="name"
          :placeholder="$t('message.selectPlaceholderText')"
          :inputPlaceholder="$t(`${pagei18nKey}.brand`)"
          class="is-fullwidth"
          :additionalInfo.sync="additionalBrandInfo"
          :options="brands"
          :class="{'is-danger': errors.has('brand')}"
          name="brand"/>
      </div>
      <p
        v-show="errors.has('brand')"
        class="help is-danger">
        {{ errors.first('brand') }}
      </p>
    </div>

    <!-- Colour -->
    <!-- todo add other option that shows text field -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.colour`) }}</label>
      <p class="control is-expanded has-icons-right"></p>
      <div class="is-fullwidth">
        <AppSelect
          v-model="colour"
          v-validate:colour="'required'"
          :data-vv-as="$t(`${pagei18nKey}.colour`)"
          displayKey="name"
          :placeholder="$t('message.selectPlaceholderText')"
          class="is-fullwidth"
          :options="colours"
          :class="{'is-danger': errors.has('colour')}"
          name="colour"/>
      </div>
      <p
        v-show="errors.has('colour')"
        class="help is-danger">
        {{ errors.first('colour') }}
      </p>
    </div>

    <!-- Construction -->
    <!-- todo add other option that shows text field -->
    <div v-show="isBuggyType === false" class="field">
      <label class="label">{{ $t(`${pagei18nKey}.material`) }}</label>
      <p class="control is-expanded has-icons-right"></p>
      <div class="is-fullwidth">
        <AppSelect
          v-model="material"
          v-on:change="onScrollHelpNeeded"
          v-validate:material="{required: !isBuggyType}"
          :data-vv-as="$t(`${pagei18nKey}.material`)"
          :inputPlaceholder="$t(`${pagei18nKey}.material`)"
          displayKey="name"
          :placeholder="$t('message.selectPlaceholderText')"
          class="is-fullwidth"
          :additionalInfo.sync="additionalMaterialInfo"
          :options="materials"
          :class="{'is-danger': errors.has('material')}"
          name="material"/>
      </div>
      <p
        v-show="errors.has('material')"
        class="help is-danger">
        {{ errors.first('material') }}
      </p>
    </div>

    <!-- Height illustartion -->
    <!-- We hide this if not asking for size or asking for lenght -->
    <div v-show="shouldHideSize === false && preferLength == false" class="baggage_dimensions_illustration">
      <img
        src="/static/theme/img/info-icon.svg"
        class="baggage_dimensions_illustration__info_icon"
        :alt="$t(`${pagei18nKey}.dimensionsInfoIconAlt`)">
      <img
        src="/static/theme/img/baggage-dims-illustration.svg"
        class="baggage_dimensions_illustration__baggage_icon"
        :alt="$t(`${pagei18nKey}.dimensionsBaggageIconAlt`)">
      <div class="baggage_dimensions_illustration__text">
        <p>{{ $t(`${pagei18nKey}.dimensionsIllustration`) }}</p>
      </div>
    </div>

    <div id="scroll-helper-target"></div>
    <!-- Height -->
    <div v-show="shouldHideSize === false" class="field">
      <label class="label">{{ preferLength ? $t(`${pagei18nKey}.length`) : $t(`${pagei18nKey}.height`) }}</label>
      <p class="control is-expanded has-icons-right"></p>
      <div class="is-fullwidth">
        <AppSelect
          v-model="height"
          v-validate:height="{required: !shouldHideSize}"
          :data-vv-as="preferLength ? $t(`${pagei18nKey}.length`) : $t(`${pagei18nKey}.height`)"
          displayKey="name"
          :placeholder="$t('message.selectPlaceholderText')"
          :inputPlaceholder="preferLength ? $t(`${pagei18nKey}.length`) : $t(`${pagei18nKey}.height`)"
          class="is-fullwidth"
          :additionalInfo.sync="additionalHeightInfo"
          :additionalInfoIsNumber="true"
          :options="heights"
          :class="{'is-danger': errors.has('height')}"
          name="height"/>
      </div>
      <p
        v-show="errors.has('height')"
        class="help is-danger">
        {{ errors.first('height') }}
      </p>
    </div>

    <!-- Wheels -->
    <div v-show="isBuggyType === false" class="field">
      <label class="label">{{ $t(`${pagei18nKey}.wheels`)}}</label>
      <p class="control is-expanded has-icons-right"></p>
      <div class="is-fullwidth">
        <AppSelect
          v-model="wheel"
          v-validate:wheel="{required: !isBuggyType}"
          :data-vv-as="$t(`${pagei18nKey}.wheels`)"
          :inputPlaceholder="$t(`${pagei18nKey}.wheels`)"
          displayKey="name"
          :placeholder="$t('message.selectPlaceholderText')"
          class="is-fullwidth"
          :additionalInfo.sync="additionalWheelInfo"
          :options="wheels"
          :class="{'is-danger': errors.has('wheels')}"
          name="wheels"/>
      </div>
      <p
        v-show="errors.has('wheels')"
        class="help is-danger">
        {{ errors.first('wheels') }}
      </p>
    </div>

    <!-- Locks -->
    <div v-show="shouldHideLocks !== true" class="field">
      <label class="label">{{ $t(`${pagei18nKey}.locks`)}}</label>
      <p class="control is-expanded has-icons-right"></p>
      <div class="is-fullwidth">
        <AppSelect
          v-model="lock"
          v-validate:lock="{required: !shouldHideLocks}"
          :data-vv-as="$t(`${pagei18nKey}.locks`)"
          :inputPlaceholder="$t(`${pagei18nKey}.locks`)"
          displayKey="name"
          :placeholder="$t('message.selectPlaceholderText')"
          class="is-fullwidth"
          :additionalInfo.sync="additionalLockInfo"
          :options="locks"
          :class="{'is-danger': errors.has('locks')}"
          name="locks"/>
      </div>
      <p
        v-show="errors.has('locks')"
        class="help is-danger">
        {{ errors.first('locks') }}
      </p>
    </div>

    <!-- Damage type -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.damageType`)}}</label>
      <p class="control is-expanded has-icons-right"></p>
      <div class="is-fullwidth">
        <AppSelect
          v-model="damageType"
          v-validate:damageType="'required'"
          :data-vv-as="$t(`${pagei18nKey}.damageType`)"
          :inputPlaceholder="$t(`${pagei18nKey}.damageType`)"
          displayKey="name"
          :placeholder="$t('message.selectPlaceholderText')"
          class="is-fullwidth"
          :additionalInfo.sync="additionalDamageTypeInfo"
          :options="damageTypes"
          :class="{'is-danger': errors.has('damageType')}"
          name="damageType"/>
      </div>
      <p
        v-show="errors.has('damageType')"
        class="help is-danger">
        {{ errors.first('damageType') }}
      </p>
    </div>

    <!-- Proof of damages -->
    <div class="section_subtitle" v-if="showImagesUpload">{{ $t(`${pagei18nKey}.proofOfDamagesTitle`) }}</div>

    <!-- Static image uploads -->
    <ms-image-upload-wrapper
      class="image_upload"
      v-if="wholeCaseUpload && showImagesUpload"
      :showControls="item.images.wholeCase !== undefined"
      :defaultImageSrc="wholeCaseUpload.defaultImageSrc"
      :imageUrl="getWholeCaseImage()"
      :caption="isBuggyType ? wholeCaseUpload.captionBuggyType: wholeCaseUpload.caption"
      :uploadFolder="claimId"
      @imageUploadSuccess="onWholeCaseUploadSuccess(wholeCaseUpload, ...arguments)"
      @imageDeleteClick="onWholeCaseUploadDelete()"
    />

    <ms-image-upload-wrapper
      class="image_upload"
      v-if="badgeUpload && showImagesUpload"
      :showControls="item.images.badge !== undefined"
      :defaultImageSrc="badgeUpload.defaultImageSrc"
      :imageUrl="getBadgeImage()"
      :caption="isBuggyType ? badgeUpload.captionBuggyType: badgeUpload.caption"
      :uploadFolder="claimId"
      @imageUploadSuccess="onBadgeUploadSuccess(badgeUpload, ...arguments)"
      @imageDeleteClick="onBadgeUploadDelete()"
    />

    <ms-image-upload-wrapper
      class="image_upload"
      v-if="baggageTagUpload && showImagesUpload"
      :showControls="item.images.baggageTag !== undefined"
      :defaultImageSrc="baggageTagUpload.defaultImageSrc"
      :imageUrl="getBaggageTagImage()"
      :caption="baggageTagUpload.caption"
      :uploadFolder="claimId"
      @imageUploadSuccess="onBaggageTagUploadSuccess(baggageTagUpload, ...arguments)"
      @imageDeleteClick="onBaggageTagUploadDelete()"
    />

    <!-- Area of damage image upload -->
    <ms-image-upload-wrapper
      class="image_upload"
      v-if="areaOfDamageUpload && showImagesUpload"
      v-for="(upload, index) in item.areaOfDamageImages"
      :key="`area-of-damage-${index}`"
      :defaultImageSrc="upload.defaultImageSrc"
      :imageUrl="getAreaOfDamageImage(upload)"
      :file="upload.file"
      :showControls="currentItem && currentItem.areaOfDamageImages.length > 0"
      :caption="upload.caption"
      :uploadFolder="claimId"
      :showSelect="upload.showSelect"
      :selectPlaceholder="upload.selectPlaceholder"
      :selectDisplayKey="upload.selectDisplayKey"
      :selectVal.sync="upload.selectVal"
      :selectOptions="upload.selectOptions"
      @imageUploadSuccess="onDamagedAreaUploadSuccess(index, ...arguments)"
      @imageDeleteClick="onAreaOfDamageImageDelete(upload, index)"
    />

    <!-- More photos button -->
    <ms-file-input-button
      v-if="moreUploadsButton.show && showImagesUpload"
      acceptedTypes="image/*"
      class="more_uploads_button"
      :button="moreUploadsButton"
      @change="onMoreUploadsChange"/>

    <div class="section_subtitle">{{ $t(`${pagei18nKey}.costTitle`) }}</div>

    <!-- Price -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.price`)}}</label>
      <p class="control is-expanded has-icons-right"></p>
      <div class="is-fullwidth">
        <AppSelect
          v-model="price"
          v-validate:price="'required'"
          :data-vv-as="$t(`${pagei18nKey}.price`)"
          :inputPlaceholder="$t(`${pagei18nKey}.price`)"
          displayKey="name"
          :placeholder="$t('message.selectPlaceholderText')"
          class="is-fullwidth"
          :additionalInfo.sync="additionalPriceInfo"
          :additionalInfoIsNumber="true"
          :options="prices"
          :class="{'is-danger': errors.has('price')}"
          name="price"/>
      </div>
      <p
        v-show="errors.has('price')"
        class="help is-danger">
        {{ errors.first('price') }}
      </p>
    </div>

    <!-- Date purchased -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.datePurchased`) }}</label>
      <ms-datetime-picker
        v-model="purchaseDate"
        :locale="locale"
        :placeholder="$t('message.datePlaceholderText')"
        :type="datePickerType"
        :maxDatetime="maxDatetime"/>
    </div>

    <!-- Add a receipt button -->
    <ms-file-input-button
      v-if="getReceiptImage() === null && getReceiptFile() === null && showImagesUpload"
      class="add_a_receipt__button"
      :button="this.addReceiptButton"
      @change="onAddReceiptClick"/>

    <ms-image-upload
      class="image_upload"
      v-if="(getReceiptFile() || getReceiptImage()) && showImagesUpload"
      :imageUrl="getReceiptImage()"
      :noImageSrc="''"
      :showControls="true"
      :file="getReceiptFile()"
      :uploadFolder="claimId"
      @uploadSuccess="onReceiptUploadSuccess(...arguments)"
      @deleteClick="onReceiptImageDelete()"/>

    <p v-if="showImagesUpload" class="add_receipt_small_print">{{ $t(`${pagei18nKey}.receiptNote`) }}</p>
  </div>
</template>

<script>
  import api from '@/api/api'
  import AppSelect from '@/components/app-select'
  import AppSettings from '@/app-settings.json'
  import MsNavigationBar from '@/components/ms-navigation-bar'
  import AppButton, {ButtonStates, ButtonTypes} from '@/components/app-button'
  import MsDatetimePicker, {DatetimeTypes} from '@/components/ms-datetime-picker'
  import MsPriceInput from '@/components/ms-price-input'
  import MsImageUploadWrapper from '@/components/ms-image-upload-wrapper'
  import MsImageUpload from '@/components/ms-image-upload'
  import MsFileInputButton from '@/components/ms-file-input-button'
  import themeSettings from '../../theme-settings.json'

  export default {
    inject: ['$validator'],
    name: 'BaggageItemForm',
    components: {
      AppButton,
      AppSelect,
      MsDatetimePicker,
      MsPriceInput,
      MsImageUploadWrapper,
      MsImageUpload,
      MsFileInputButton,
      MsNavigationBar
    },

    props: {
      claimId: String,
      currentItem: Object,
      currentItemIndex: Number,
      addToClaim: {
        type: Boolean,
        default: false
      },
      imagesRequired: {
        type: Boolean,
        default: false
      },
      showImagesUpload: {
        type: Boolean,
        default: true
      }
    },

    data () {
      return {
        isKiosk: !!AppSettings.kioskSettings,
        wholeCaseUpload: {
          caption: this.$t('message.baggageItem.wholeCaseUpload'),
          captionBuggyType: this.$t('message.baggageItem.wholeCaseUploadBuggyType'),
          defaultImageSrc: '/static/theme/img/whole-case-photo.png'
        },
        badgeUpload: {
          caption: this.$t('message.baggageItem.badgeUpload'),
          captionBuggyType: this.$t('message.baggageItem.badgeUploadBuggyType'),
          defaultImageSrc: '/static/theme/img/brand-badge-photo.png'
        },
        baggageTagUpload: {
          caption: this.$t('message.baggageItem.baggageTagUpload'),
          defaultImageSrc: '/static/theme/img/baggage-tag-photo.png'
        },
        areaOfDamageUpload: {
          caption: this.$t('message.baggageItem.areaOfDamageUpload.caption'),
          defaultImageSrc: '/static/theme/img/area-of-damage-photo.png',
          selectPlaceholder: this.$t('message.baggageItem.areaOfDamageUpload.selectPlaceholder'),
          selectDisplayKey: 'option',
          selectOptions: [
            {
              option: this.$t('message.baggageItem.areaOfDamageUpload.selectOptions.top')
            },
            {
              option: this.$t('message.baggageItem.areaOfDamageUpload.selectOptions.bottom')
            },
            {
              option: this.$t('message.baggageItem.areaOfDamageUpload.selectOptions.left')
            },
            {
              option: this.$t('message.baggageItem.areaOfDamageUpload.selectOptions.right')
            }
          ],
          selectVal: null,
          showSelect: false
        },
        receiptUpload: {
          imageUrl: null,
          file: null
        },
        copiedItem: {},
        pagei18nKey: 'message.baggageItem',
        moreUploadsButton: {
          show: this.currentItem && this.currentItem.areaOfDamageImages !== undefined,
          state: ButtonStates.ENABLED,
          type: ButtonTypes.QUINARY,
          hasIconLeft: true,
          text: 'message.baggageItem.morePhotos'
        },
        currencies: [],
        types: [],
        brands: [],
        colours: [],
        materials: [],
        locks: [],
        wheels: [],
        damageTypes: [],
        prices: [],
        heights: [],
        item: {
          tagNumber: '',
          brand: null,
          brandString: '',
          additionalBrandInfo: '',
          type: null,
          typeString: '',
          colour: null,
          colourString: '',
          height: null,
          heightString: '',
          additionalHeightInfo: '',
          material: null,
          materialString: '',
          additionalMaterialInfo: '',
          lock: null,
          lockString: '',
          additionalLockInfo: '',
          wheel: null,
          wheelString: '',
          additionalWheelInfo: '',
          damageType: null,
          damageTypeString: '',
          additionalDamageTypeInfo: '',
          purchaseDate: null,
          images: {},
          areaOfDamageImages: [],
          receipt: null,
          price: null,
          priceString: '',
          additionalPriceInfo: '',
          replacementVariantId: null,
          replacementChosen: false,
          isExpandable: false,
          serialNumber: '',
          baggageModel: ''
        },
        replacementLuggageOptionEnable: themeSettings.theme.replacementLuggageOptionEnable,
        isBuggyType: undefined,
        hideLocks: undefined,
        hideSize: undefined,
        preferLength: undefined,
        tagNumberRequired: !!themeSettings.theme.tagNumberRequired
      }
    },

    beforeMount () {
      if (this.currentItem) {
        for (let prop in this.item) {
          this.item[prop] = this.currentItem[prop]
        }
      }
    },

    mounted () {
      // Push into area of damage uploads array first upload object
      if (!this.currentItem) {
        this.item.areaOfDamageImages.push({ ...this.areaOfDamageUpload })
        this.item.receipt = this.receiptUpload
      } else {
        // We emit purchase date change on mount to see if we have a
        // value (usefully when editting an item)
        this.$emit('purchaseDateChange', this.purchaseDate)
        this.imageValidityCheck()
        this.$emit('priceInputChange', this.price)
      }
      api.getBaggagePrices()
        .then(response => {
          this.prices = response.data
        })
        .catch(console.error)

      api.getBaggageHeights()
        .then(response => {
          this.heights = response.data
        })
        .catch(console.error)

      api.getBaggageDamages()
        .then(response => {
          this.damageTypes = response.data
        })
        .catch(console.error)

      /**
       * Get baggage types data from api
       */
      api.getBaggageTypes()
        .then(response => {
          this.types = response.data
          // if we're editing a current item, then when we have the baggage types
          // we notify form baggage type has changed so we can update form to reflect
          // correct questions
          if (this.currentItem) this.onBaggageTypeChanged()
        })
        .catch(console.error)

      /**
       * Get baggage brands data from api
       */
      api.getBaggageBrands()
        .then(response => {
          this.brands = response.data
        })
        .catch(console.error)

      /**
       * Get baggage colours data from api
       */
      api.getBaggageColours()
        .then(response => {
          this.colours = response.data
        })
        .catch(console.error)

      /**
       * Get baggage materials data from api
       */
      api.getBaggageMaterials()
        .then(response => {
          this.materials = response.data
        })
        .catch(console.error)

      /**
       * Get baggage wheels data from api
       */
      api.getBaggageWheels()
        .then(response => {
          this.wheels = response.data
        })
        .catch(console.error)

      /**
       * Get baggage locks data from api
       */
      api.getBaggageLocks()
        .then(response => {
          this.locks = response.data
        })
        .catch(console.error)
    },

    watch: {
      /**
       * We watch addToClaim prop from parent to trigger
       * add item to claim function once addToClaim is true
       *
       * @param {boolean} newVal - The new value of addToClaim
       */
      addToClaim: function (newVal) {
        if (newVal) {
          this.addItemToClaim()
        }
      }
    },

    computed: {
      /**
       * Get reported button object
       */
      expandableButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: this.item.isExpandable ? ButtonTypes.PRIMARY : ButtonTypes.SECONDARY,
            text: 'message.yesButton',
            id: 'expandableButton'
          }
        }
      },

      /**
       * Get unreported button object
       */
      notExpandableButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: this.item.isExpandable ? ButtonTypes.SECONDARY : ButtonTypes.PRIMARY,
            text: 'message.noButton',
            id: 'notExpandableButton'
          }
        }
      },
      /**
       * Get date locale code
       */
      locale: {
        get () {
          const currentLanguage = this.$store.getters.language
          if (
            currentLanguage &&
            currentLanguage.dateLocaleCode
          ) {
            return currentLanguage.dateLocaleCode
          }
          return null
        }
      },

      /**
       * Get datetime type
       */
      datePickerType: {
        get () {
          return DatetimeTypes.YEAR
        }
      },

      /**
       *  Gets max datetime
       */
      maxDatetime: {
        get () {
          return new Date().getTime()
        }
      },

      type: {
        /**
         * Get current stored baggage type
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.type,
            this.types,
            'id'
          )
        },

        /**
         * Set baggage type
         *
         * @param {Object} value - the selected type object
         */
        set (value) {
          // Update the store type global id
          this.item.type = value.id

          // Update the store data with the name selected
          if (value.index !== null) {
            this.item.typeString = this.getStringValueOfObjectFromKey(
              value.id,
              this.types,
              'id',
              'name'
            )
          }
          // on type change need to tell form so UI can update accordingly
          // as we ask differnt questions for different baggage types
          this.onBaggageTypeChanged()
        }
      },

      brand: {
        /**
         * Get current stored brand
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.brand,
            this.brands,
            'id'
          )
        },

        /**
         * Set brand
         *
         * @param {Object} value - the selected brand object
         */
        set (value) {
          this.item.brand = value.id

          if (value.index !== null) {
            this.item.brandString = this.getStringValueOfObjectFromKey(
              value.id,
              this.brands,
              'id',
              'name'
            )
          }
        }
      },

      price: {
        /**
         * Get current stored price amount
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.price,
            this.prices,
            'id'
          )
        },

        /**
         * Set price amount
         *
         * @param {Object} value - The object of the selected price amount in dropdown
         */
        set (value) {
          this.item.price = value.id

          if (value.index !== null) {
            this.item.priceString = this.getStringValueOfObjectFromKey(
              value.id,
              this.prices,
              'id',
              'name'
            )
          }
        }
      },

      additionalPriceInfo: {
        /**
         *  Gets price info
         */
        get () {
          return this.item.additionalPriceInfo
        },

        /**
         * Sets custom price value
         *
         * @param {string} value - Value of input field
         */
        set (value) {
          this.item.additionalPriceInfo = value
        }
      },

      tagNumber: {
        /**
         * Get current stored tag number
         */
        get () {
          return this.item.tagNumber
        },

        /**
         * Set tag number
         *
         * @param {string} value - Value to set tag number to
         */
        set (value) {
          this.item.tagNumber = value
        }
      },

      baggageModel: {
        /**
         * Get current stored model
         */
        get () {
          return this.item.baggageModel
        },

        /**
         * Set model
         *
         * @param {string} value - Value to set model to
         */
        set (value) {
          this.item.baggageModel = value
        }
      },

      serialNumber: {
        /**
         * Get current stored serial number
         */
        get () {
          return this.item.serialNumber
        },

        /**
         * Set serial number
         *
         * @param {string} value - Value to set serial number to
         */
        set (value) {
          this.item.serialNumber = value
        }
      },

      additionalBrandInfo: {
        /**
         *  Gets current stored brand
         */
        get () {
          return this.item.additionalBrandInfo
        },

        /**
         * Sets custom brand value
         *
         * @param {string} value - Value of input field
         */
        set (value) {
          this.item.additionalBrandInfo = value
        }
      },

      colour: {
        /**
         * Get current stored colour
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.colour,
            this.colours,
            'id'
          )
        },

        /**
         * Set colour
         *
         * @param {Object} value - the selected colour object
         */
        set (value) {
          this.item.colour = value.id

          if (value.index !== null) {
            this.item.colourString = this.getStringValueOfObjectFromKey(
              value.id,
              this.colours,
              'id',
              'name'
            )
          }
        }
      },

      height: {
        /**
         * Get current stored height
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.height,
            this.heights,
            'id'
          )
        },

        /**
         * Set baggage type
         *
         * @param {Object} value - the selected type object
         */
        set (value) {
          this.item.height = value.id

          if (value.index !== null) {
            this.item.heightString = this.getStringValueOfObjectFromKey(
              value.id,
              this.heights,
              'id',
              'name'
            )
          }
        }
      },

      additionalHeightInfo: {
        /**
         *  Gets current stored brand
         */
        get () {
          return this.item.additionalHeightInfo
        },

        /**
         * Sets custom brand value
         *
         * @param {string} value - Value of input field
         */
        set (value) {
          this.item.additionalHeightInfo = value
        }
      },

      material: {
        /**
         * Get current stored material
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.material,
            this.materials,
            'id'
          )
        },

        /**
         * Set material
         *
         * @param {Object} value - The object of the selected material in dropdown
         */
        set (value) {
          this.item.material = value.id

          if (value.index !== null) {
            this.item.materialString = this.getStringValueOfObjectFromKey(
              value.id,
              this.materials,
              'id',
              'name'
            )
          }
        }
      },

      additionalMaterialInfo: {
        /**
         *  Gets current stored material
         */
        get () {
          return this.item.additionalMaterialInfo
        },

        /**
         * Sets custom material value
         *
         * @param {string} value - Value of input field
         */
        set (value) {
          this.item.additionalMaterialInfo = value
        }
      },

      wheel: {
        /**
         * Get current stored wheels amount
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.wheel,
            this.wheels,
            'id'
          )
        },

        /**
         * Set wheels amount
         *
         * @param {Object} value - The object of the selected wheels amount in dropdown
         */
        set (value) {
          this.item.wheel = value.id

          if (value.index !== null) {
            this.item.wheelString = this.getStringValueOfObjectFromKey(
              value.id,
              this.wheels,
              'id',
              'name'
            )
          }
        }
      },

      additionalWheelInfo: {
        /**
         *  Gets wheels info
         */
        get () {
          return this.item.additionalWheelInfo
        },

        /**
         * Sets custom wheels value
         *
         * @param {string} value - Value of input field
         */
        set (value) {
          this.item.additionalWheelInfo = value
        }
      },

      damageType: {
        /**
         * Get current stored damage type
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.damageType,
            this.damageTypes,
            'id'
          )
        },

        /**
         * Set damage type
         *
         * @param {Object} value - The object of the selected damage type in dropdown
         */
        set (value) {
          this.item.damageType = value.id

          if (value.index !== null) {
            this.item.damageTypeString = this.getStringValueOfObjectFromKey(
              value.id,
              this.damageTypes,
              'id',
              'name'
            )
          }
        }
      },

      additionalDamageTypeInfo: {
        /**
         *  Gets damage type info
         */
        get () {
          return this.item.additionalDamageTypeInfo
        },

        /**
         * Sets custom damage type value
         *
         * @param {string} value - Value of input field
         */
        set (value) {
          this.item.additionalDamageTypeInfo = value
        }
      },

      lock: {
        /**
         * Get current stored locks
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.lock,
            this.locks,
            'id'
          )
        },

        /**
         * Set locks
         *
         * @param {Object} value - The object of the selected locks in dropdown
         */
        set (value) {
          this.item.lock = value.id

          if (value.index !== null) {
            this.item.lockString = this.getStringValueOfObjectFromKey(
              value.id,
              this.locks,
              'id',
              'name'
            )
          }
        }
      },

      additionalLockInfo: {
        /**
         *  Gets current stored lock
         */
        get () {
          return this.item.additionalLockInfo
        },

        /**
         * Sets custom lock value
         *
         * @param {string} value - Value of input field
         */
        set (value) {
          this.item.additionalLockInfo = value
        }
      },

      purchaseDate: {
        /**
         * Get the current item purchase date
         */
        get () {
          return this.item.purchaseDate
        },

        /**
         * Set the item purchase date
         *
         * @param {number} value - Epoch of purchase date
         */
        set (value) {
          this.$emit('purchaseDateChange', value)
          this.item.purchaseDate = value
        }
      },

      /**
       * Get add a receipt button object
       */
      addReceiptButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: ButtonTypes.QUATERNARY,
            text: `${this.pagei18nKey}.receiptButton`,
            hasIconLeft: true
          }
        }
      },

      /**
       * Get new image upload button object
       */
      newImageUploadButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: ButtonTypes.TERTIARY
          }
        }
      },

      shouldHideLocks: {
        get () {
          return this.hideLocks === true || this.isBuggyType === true
        }
      },

      shouldHideSize: {
        get () {
          return this.hideSize === true || this.isBuggyType === true
        }
      }
    },

    methods: {

      /**
       * Call each time baggage type changes in order to keep form questions
       * in sync for this baggage type
       */
      onBaggageTypeChanged: function () {
        // safety play
        if (!this.types || !this.item.type) return

        // get the baggage type object and update the form to reflect the correct
        // questions for baggage item selected. E.g. we ask slightly different
        // questions for a general bag over things considered 'buggy or mobility'
        let foundItem = this.types.find(item => {
          return item['id'] === this.item.type
        })
        foundItem.isBuggyType === true ? this.isBuggyType = true : this.isBuggyType = false
        foundItem.hideLocks === true ? this.hideLocks = true : this.hideLocks = false
        foundItem.preferLength === true ? this.preferLength = true : this.preferLength = false
        foundItem.hideSize === true ? this.hideSize = true : this.hideSize = false
      },

      /**
       * Called wheneer the Brand selection value changes
       */
      onBrandChanged: function () {
        // When user changes the brand we check if we're on kioks and if buggy
        // type as if this is true we're far enough down the form that it is helpful
        // to auto-scroll the rest of the form into view
        if (this.isKiosk && this.isBuggyType) this.onScrollHelpNeeded()
      },

      /**
       * Helper to scroll bottom half of form into view
       * Only used on kiosk as a way to inform user to scroll down
       * Should be called when user is roughly half way down the form.
       */
      onScrollHelpNeeded: function () {
        if (this.isKiosk) {
          document.getElementById('scroll-helper-target').scrollIntoView({behavior: 'smooth'})
        }
      },

      /**
       * Set isReported bool based on what button was clicked
       *
       * @param {string} buttonId - The id of the clicked button
       */
      expandableButtonClick: function (buttonId) {
        if (buttonId === this.expandableButton.id) {
          this.item.isExpandable = true
        } else if (buttonId === this.notExpandableButton.id) {
          this.item.isExpandable = false
        }
      },
      /**
       * Add new area of damage uploads for each file in event
       *
       * @param {Event} event - the input file change event
       */
      onMoreUploadsChange (event) {
        let newUploads = []
        const files = Array.from(event.target.files)

        files.forEach((file) => {
          newUploads.push(
            Object.assign(
              JSON.parse(JSON.stringify(this.areaOfDamageUpload)),
              {
                file: file
              }
            )
          )
        })

        this.item.areaOfDamageImages.push(...newUploads)
      },

      /**
       * Remove the image from the whole case upload
       */
      onWholeCaseUploadDelete () {
        this.item.images.wholeCase.imageUrl = null
        this.imageValidityCheck()
      },

      /**
       * Remove the image from the badge upload
       */
      onBadgeUploadDelete () {
        this.item.images.badge.imageUrl = null
        this.imageValidityCheck()
      },

      /**
       * Remove the image from the baggage tag upload
       */
      onBaggageTagUploadDelete () {
        this.item.images.baggageTag.imageUrl = null
        this.imageValidityCheck()
      },

      /**
       * Remove the image from the receipt upload
       */
      onReceiptImageDelete () {
        this.item.receipt.imageUrl = null
        this.item.receipt.file = null
        this.imageValidityCheck()
      },

      /**
       * Get the badge upload image url
       */
      getBadgeImage () {
        if (this.currentItem) {
          let storedBadgeImage = this.item.images.badge
          if (storedBadgeImage && storedBadgeImage.imageUrl) {
            return storedBadgeImage.imageUrl
          }
          return null
        }
      },

      /**
       * Get the baggage tag upload image url
       */
      getBaggageTagImage () {
        if (this.currentItem) {
          let storedBaggageTagImage = this.item.images.baggageTag
          if (storedBaggageTagImage && storedBaggageTagImage.imageUrl) {
            return storedBaggageTagImage.imageUrl
          }
          return null
        }
      },

      /**
       * Get the whole case upload image url
       */
      getWholeCaseImage () {
        if (this.item.images.wholeCase) {
          let storedWholeCaseImage = this.item.images.wholeCase
          if (storedWholeCaseImage && storedWholeCaseImage.imageUrl) {
            return storedWholeCaseImage.imageUrl
          }
          return null
        }
      },

      /**
       * Get an area of damage upload's image url
       *
       * @param {string} upload - The upload object to get image url for
       */
      getAreaOfDamageImage (upload) {
        return upload.imageUrl ? upload.imageUrl : null
      },

      /**
       * Get receipt's image url
       */
      getReceiptImage () {
        if (this.item.receipt) {
          return this.item.receipt.imageUrl ? this.item.receipt.imageUrl : null
        }
        return null
      },

      /**
       * Get receipt's file
       */
      getReceiptFile () {
        if (this.item.receipt) {
          return this.item.receipt.file ? this.item.receipt.file : null
        }
        return null
      },

      /**
       * On add a receipt button
       */
      onAddReceiptClick (event) {
        this.item.receipt = Object.assign(this.receiptUpload, { 'file': event.target.files[0] })
      },

      /**
       * Add item to claim
       */
      addItemToClaim () {
        let completedItem
        let index
        if (this.currentItem) {
          // we're updating an existing item
          // Deep clone item
          completedItem = JSON.parse(JSON.stringify({
            data: this.item,
            index: this.currentItemIndex
          }))
          this.$store.commit('replaceBaggageItem', completedItem)
          index = this.currentItemIndex
        } else {
          // we're creating a new item
          this.$store.commit('addBaggageItem', this.item)
          index = this.$store.getters.baggageItems.length - 1
        }

        // this.$router.replace('/claims-items-summary')
        if (this.replacementLuggageOptionEnable) {
          // for baggage we try to offer a replacement
          this.$router.replace(`/replacement-item/baggageItems/${index}`)
        } else {
          this.$router.replace('/claims-items-summary')
        }
      },

      // todo logic for validation
      /**
       * on image upload success for individual upload fields
       * push image object into the item's images
       *
       * @param {string} url - Url of uploaded image
       */
      onWholeCaseUploadSuccess (upload, url) {
        this.item.images.wholeCase = Object.assign(upload, { 'imageUrl': url })
        this.imageValidityCheck()
      },

      /**
       * on image upload success for individual upload fields
       * push image object into the item's images
       *
       * @param {string} url - Url of uploaded image
       */
      onBadgeUploadSuccess (upload, url) {
        this.item.images.badge = Object.assign(upload, { 'imageUrl': url })
        this.imageValidityCheck()
      },

      /**
       * on image upload success for individual upload fields
       * push image object into the item's images
       *
       * @param {string} url - Url of uploaded image
       */
      onBaggageTagUploadSuccess (upload, url) {
        this.item.images.baggageTag = Object.assign(upload, { 'imageUrl': url })
        this.imageValidityCheck()
      },

      onReceiptUploadSuccess (url) {
        this.item.receipt = Object.assign(this.item.receipt, { 'imageUrl': url })

        // Remove file so, on edit file wouldn't get initialised once again
        this.item.receipt.file = null
        this.imageValidityCheck()
      },

      /**
       * On damaged area image upload success push the relevant details
       * into the current upload item to be pushed into images array on submit
       *
       * @param {number} index - Index of upload object in areaOfDamageUploads array
       * @param {string} url - Url of uploaded image
       */
      onDamagedAreaUploadSuccess (index, url) {
        // Show the select options
        this.item.areaOfDamageImages[index].showSelect = true

        // Format the data we need to pass to the store later on
        this.item.areaOfDamageImages[index].imageUrl = url

        // If the more uploads button isn't already showing then show it
        if (!this.moreUploadsButton.show) {
          this.moreUploadsButton.show = true
        }

        // After initial upload, set file to null to avoid additional initiliasation on edit
        this.item.areaOfDamageImages[index].file = null

        this.imageValidityCheck()
      },

      /**
       * On area of damage image delete click, reset uploader field or delete
       * item entirely if it is not the first item
       *
       * @param {Object} upload - The upload object for the deleted image item
       * @param {number} index - The index of the image in areaOfDamageImages array
       */
      onAreaOfDamageImageDelete (upload, index) {
        if (index > 0) {
          this.item.areaOfDamageImages.splice(index, 1)
        } else {
          upload.imageUrl = null
          upload.showSelect = false
          upload.showControls = false
          upload.selectVal = null
        }

        this.moreUploadsButton.show = this.item.areaOfDamageImages[0].imageUrl
        this.imageValidityCheck()
      },

      /**
       * Get the index of an object that has a matching key
       *
       * @param {string|number} value - Value to match
       * @param {Object[]} data - The data to check in
       * @param {string} key - The key to check value in each object
       */
      getIndexOfObjectFromKey: function (value, data, key) {
        let foundIndex = data.findIndex(item => {
          return item[key] === value
        })

        return foundIndex === -1 ? value : foundIndex
      },

      /**
       * Get string value based on matching another key value
       *
       * @param {string|number} value - Value to match
       * @param {Object[]} data - Data to search in
       * @param {string} searchKey - Key to match value in
       * @param {string} stringKey - Key to get string value
       */
      getStringValueOfObjectFromKey: function (value, data, searchKey, stringKey) {
        let foundItem = data.find(item => {
          return item[searchKey] === value
        })

        return foundItem[stringKey]
      },

      /**
       * Check whether the minimum images are uploaded and fire an emit
       * with valid value
       */
      imageValidityCheck: function () {
        // if images not required then emit event
        if (!this.imagesRequired) {
          this.$emit('imageValidityCheck', true)
          return// exit
        }

        if (
          this.item.images.wholeCase &&
          this.item.images.badge &&
          // this.item.images.baggageTag && - this one optional
          this.item.areaOfDamageImages[0]
        ) {
          const imageFields = [
            this.item.images.wholeCase.imageUrl,
            this.item.images.badge.imageUrl,
            // this.item.images.baggageTag.imageUrl, - this one optional
            this.item.areaOfDamageImages[0].imageUrl
          ]
          if (imageFields.every((image) => image !== undefined && image !== null)) {
            this.$emit('imageValidityCheck', true)
          } else {
            this.$emit('imageValidityCheck', false)
          }
        } else {
          this.$emit('imageValidityCheck', false)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../assets/sass/main.scss";

  .section_subtitle {
    @extend .subtitle;
    padding-top: $dims-spacing-large;
    margin-top: $dims-spacing-large;
    border-top: $default-border-style;
  }

  .baggage_dimensions_illustration {
    display: flex;
    align-items: flex-start;
    margin: $dims-spacing-large 0;
  }

  .baggage_dimensions_illustration__info_icon,
  .baggage_dimensions_illustration__baggage_icon {
    margin-right: $dims-spacing-small;
  }

  .baggage_dimensions_illustration__info_icon {
    width: $dims-baggage-dimensions-info-icon-width;
    height: 100%;
  }

  .baggage_dimensions_illustration__baggage_icon {
    width: $dims-baggage-dimensions-icon-width;
    height: 100%;
  }

  .baggage_dimensions_illustration__text {
    display: inline-flex;
    align-items: center;
  }

  .image_upload {
    margin-bottom: $dims-spacing-small;
  }

  .more_uploads_button {
    ::v-deep .app_button .app_button__icon_left {
      height: $dims-more-photo-uploads-button-icon-size;
      width: $dims-more-photo-uploads-button-icon-size;
    }
  }

  .add_a_receipt__button {
    max-width: none;
    margin: $dims-spacing-small auto $dims-spacing-small auto;
    padding: $dims-spacing-small 0;

    ::v-deep .app_button__icon_left {
      height: $dims-add-receipt-button-icon-size;
      width: $dims-add-receipt-button-icon-size;
    }

    ::v-deep .app_button {
      margin-top: 0;
    }
  }

  .add_receipt_small_print {
    margin-bottom: $dims-spacing-small;
  }

  .cost_input {
    margin-bottom: $dims-spacing-large;
  }
</style>
